/**
 * UserAvatar — single, mandatory rendering primitive for profile avatars.
 *
 * Why this exists:
 *   The `avatars` storage bucket is PRIVATE. Profile avatars are stored as
 *   relative paths (`<uid>/avatar.<ext>`) in `profiles.avatar_url`. They MUST
 *   be resolved to short-lived signed URLs at render time.
 *
 * Rules enforced by an architecture test:
 *   No file outside `src/services/profile/` and `src/components/identity/`
 *   may render `<img src={...avatar_url}>` directly. Always use this component.
 *
 * Unified API (preferred over ad-hoc wrappers):
 *   - `size`:  "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | number (px)
 *              → produces consistent width/height + scaled initials.
 *   - `shape`: "circle" | "rounded" | "square"
 *              → unified corner radius across the app.
 *   - `tooltip`: ReactNode shown on hover/focus (uses the design-system
 *                Tooltip primitive). Pass a string for the typical case.
 *   - `ring`: optional 1px border in the standard `border` color.
 *
 *   Legacy `className` is still respected and merged last so callers can
 *   override (e.g. add `ring-1 ring-border`). Prefer the props above.
 *
 * Fallback strategy (in order):
 *   1. Resolved signed URL renders as `<img>`.
 *   2. If signing fails OR the `<img>` errors at runtime → render `fallback`.
 *   3. If no `fallback` is provided, render the built-in default:
 *        a. Initials computed from `displayName` (and `alt` as a backup), OR
 *        b. a neutral user icon when no name material is available.
 *      The default fallback inherits the component's `className` so size,
 *      shape, and ring stay consistent with the resolved-image state.
 *
 * Usage:
 *   <UserAvatar path={profile.avatar_url} size="md" displayName={profile.display_name} />
 *   <UserAvatar path={profile.avatar_url} size="lg" shape="rounded" tooltip={profile.display_name} />
 *   <UserAvatar path={null} size="sm" fallback={<CustomIcon />} />
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { User as UserIcon } from "lucide-react";
import {
  invalidateAvatarCache,
  isAvatarPathUploadProtected,
  peekAvatarUrl,
  resolveAvatarUrl,
} from "@/services/profile";
import { AVATAR_INVALIDATED_EVENT } from "@/services/profile/avatarStorageService";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Named size tokens. Use these instead of hard-coded `w-X h-X` classes. */
export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | number;

/** Visual shape. `rounded` ≈ 2xl rounded square (used in profile/buddy cards). */
export type AvatarShape = "circle" | "rounded" | "square";

const SIZE_PX: Record<Exclude<AvatarSize, number>, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 64,
  "2xl": 80,
};

const SHAPE_CLASS: Record<AvatarShape, string> = {
  circle: "rounded-full",
  rounded: "rounded-2xl",
  square: "rounded-none",
};

function resolveSizePx(size: AvatarSize): number {
  return typeof size === "number" ? size : SIZE_PX[size];
}

export interface UserAvatarProps {
  /** The raw `profiles.avatar_url` value (a storage path; legacy URLs are tolerated). */
  path: string | null | undefined;
  alt?: string;
  /** Named token (`xs`–`2xl`) or pixel value. Default: `md`. */
  size?: AvatarSize;
  /** Visual shape. Default: `circle`. */
  shape?: AvatarShape;
  /** Optional 1px ring in the standard `border` color. Default: false. */
  ring?: boolean;
  /**
   * Optional tooltip shown on hover/focus. Strings are rendered as plain text;
   * pass a node for richer content. When set, the avatar is wrapped in a
   * focusable trigger so screen-reader / keyboard users get the same hint.
   */
  tooltip?: ReactNode;
  /** Legacy escape hatch — merged AFTER size/shape so callers can still tweak. */
  className?: string;
  /**
   * Optional custom fallback rendered when `path` is empty/invalid OR signing
   * or image loading fails. If omitted, the built-in initials/icon fallback
   * is used (see component docs).
   */
  fallback?: ReactNode;
  /**
   * Display name used to derive initials for the built-in fallback. Ignored
   * when an explicit `fallback` is provided.
   */
  displayName?: string | null;
  /** Optional explicit `loading` strategy for the underlying `<img>`. */
  loading?: "lazy" | "eager";
  /**
   * Whether to render a subtle skeleton while the signed URL is being
   * resolved (defaults to `true`). Set to `false` to render the
   * initials/icon fallback immediately during loading instead.
   */
  showLoadingState?: boolean;
}

/**
 * Compute up to 2 uppercase initials from a free-text name.
 * - "Anna Müller"     → "AM"
 * - "anna"            → "A"
 * - "Anna-Maria Wolf" → "AW"
 * - "  "              → null
 */
function computeInitials(...sources: Array<string | null | undefined>): string | null {
  for (const raw of sources) {
    const value = (raw ?? "").trim();
    if (!value) continue;
    // Split on whitespace and common separators; drop empty tokens.
    const tokens = value
      .split(/[\s._\-/\\]+/u)
      .map((t) => t.trim())
      .filter(Boolean);
    if (tokens.length === 0) continue;
    const first = [...tokens[0]][0] ?? "";
    const last =
      tokens.length > 1 ? ([...tokens[tokens.length - 1]][0] ?? "") : "";
    const initials = (first + last).toUpperCase();
    if (initials) return initials;
  }
  return null;
}

/**
 * Tiny non-cryptographic string hash (djb2-ish). Stable across re-renders for
 * the same input string, so an unchanged signed URL produces an unchanged
 * `v=` cache-buster (no spurious image re-downloads on every render).
 */
function hashString(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  // Unsigned, base36 — short and URL-safe.
  return (h >>> 0).toString(36);
}

function DefaultAvatarFallback({
  className,
  initials,
  ariaLabel,
  style,
}: {
  className?: string;
  initials: string | null;
  ariaLabel: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={style}
      className={cn(
        "bg-muted text-muted-foreground select-none",
        "font-medium overflow-hidden",
        className,
        // Display class MUST come after `className` so callers passing
        // `inline-block` (e.g. via the avatar baseClass) cannot win the
        // tailwind-merge tie and break centering of the initials.
        "flex items-center justify-center",
      )}
    >
      {initials ? (
        <span
          className="leading-none tracking-wide"
          style={{ fontSize: "0.45em" }}
        >
          {initials}
        </span>
      ) : (
        <UserIcon className="w-1/2 h-1/2" aria-hidden />
      )}
    </div>
  );
}

/**
 * Skeleton placeholder shown while the signed URL is in-flight. Matches the
 * avatar's box exactly (size + shape) so layout never shifts on resolve.
 */
function AvatarSkeleton({
  className,
  style,
  ariaLabel,
}: {
  className?: string;
  style?: React.CSSProperties;
  ariaLabel: string;
}) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      style={style}
      className={cn(
        "bg-muted animate-pulse overflow-hidden",
        className,
      )}
    />
  );
}

export function UserAvatar({
  path,
  alt = "",
  size = "md",
  shape = "circle",
  ring = false,
  tooltip,
  className,
  fallback,
  displayName,
  loading = "lazy",
  showLoadingState = true,
}: UserAvatarProps) {
  // Seed from the synchronous cache so cache hits skip the loading skeleton
  // entirely — avoids a one-frame placeholder flash on remount / navigation.
  const initialUrl = path ? peekAvatarUrl(path) : null;
  const [url, setUrl] = useState<string | null>(initialUrl);
  const [loadingState, setLoadingState] = useState<boolean>(
    Boolean(path) && initialUrl === null,
  );
  const [failed, setFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const lastPathRef = useRef<string | null | undefined>(undefined);
  // Always reflects the latest `path` prop. Used by the invalidation
  // listener so it can be registered exactly once (path-independent) and
  // still see the current binding without a re-subscribe gap.
  const pathRef = useRef<string | null | undefined>(path);
  useEffect(() => {
    pathRef.current = path;
  }, [path]);
  // Tracks whether we already attempted a one-shot re-resolve for the current
  // `path`. Reset whenever `path` changes so a new avatar gets a fresh chance.
  const retriedRef = useRef(false);
  // Bumped whenever the avatar at `path` is replaced in storage. Triggers a
  // re-resolve even when `path` itself is unchanged (the "replace" case).
  const [reloadKey, setReloadKey] = useState(0);
  // Tracks any pending background retry timer so we can cancel it on unmount
  // or when `path` changes mid-retry. Avoids stale state updates and double
  // resolves when the user navigates between profiles quickly.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearRetryTimer = () => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };
  // Once the retry budget for the current `path` is used up, latch this so
  // the `<img onError>` handler stops kicking off NEW retry chains. Without
  // this, an unresolvable storage object (e.g. wrong extension persisted in
  // `profiles.avatar_url`) would loop forever: signed URL → 404 → onError →
  // invalidate → new signed URL → 404 → … producing the visible "zucken".
  // Reset whenever `path` changes (the new path deserves a fresh chance).
  const retriesExhaustedRef = useRef(false);
  // Tracks if a retry chain is currently in-flight, so concurrent triggers
  // (initial resolve + onError + invalidation event) don't stack multiple
  // chains and cause repeated url state churn.
  const retryInFlightRef = useRef(false);

  /**
   * Schedule up to N background retries for resolving a signed URL when the
   * initial attempt (or a post-event re-resolve) returned `null`. This
   * recovers from transient Storage/JWT-signing hiccups without requiring a
   * page refresh — the avatar simply pops in a few seconds later.
   *
   * Backoff: 1s, 2s, 4s (capped). The cache for `targetPath` is invalidated
   * before each retry so we don't keep re-reading a poisoned `null` entry.
   */
  const scheduleResolveRetry = (
    targetPath: string,
    attempt: number,
    onResolved: (url: string) => void,
  ) => {
    const MAX_ATTEMPTS = 3;
    if (attempt > MAX_ATTEMPTS) {
      retryInFlightRef.current = false;
      retriesExhaustedRef.current = true;
      return;
    }
    retryInFlightRef.current = true;
    const delayMs = Math.min(1000 * 2 ** (attempt - 1), 4000);
    clearRetryTimer();
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      // Bail if the prop has since moved to an unrelated path.
      const liveCurrent = pathRef.current;
      if (!liveCurrent) {
        retryInFlightRef.current = false;
        return;
      }
      const stripExt = (s: string) =>
        s.replace(/\.(jpg|jpeg|png|webp)$/i, "");
      if (
        targetPath !== liveCurrent &&
        stripExt(targetPath) !== stripExt(liveCurrent)
      ) {
        retryInFlightRef.current = false;
        return;
      }
      invalidateAvatarCache(targetPath);
      void resolveAvatarUrl(targetPath).then((resolved) => {
        const stillLive = pathRef.current;
        if (!stillLive) {
          retryInFlightRef.current = false;
          return;
        }
        if (
          targetPath !== stillLive &&
          stripExt(targetPath) !== stripExt(stillLive)
        ) {
          retryInFlightRef.current = false;
          return;
        }
        if (resolved) {
          retryInFlightRef.current = false;
          retriesExhaustedRef.current = false;
          setLoadingState(false);
          setFailed(false);
          onResolved(resolved);
          return;
        }
        scheduleResolveRetry(targetPath, attempt + 1, onResolved);
      });
    }, delayMs);
  };

  // Helper: only schedule a retry chain when one isn't already running and
  // the budget for the current path hasn't been exhausted. Prevents the
  // overlapping-chains case that produced the visible avatar flicker.
  const tryScheduleRetry = (
    targetPath: string,
    onResolved: (url: string) => void,
  ) => {
    if (retriesExhaustedRef.current) return;
    if (retryInFlightRef.current) return;
    scheduleResolveRetry(targetPath, 1, onResolved);
  };

  // Always cancel any in-flight retry timer when the component unmounts.
  useEffect(() => clearRetryTimer, []);

  useEffect(() => {
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    setFailed(false);
    retriedRef.current = false;
    retriesExhaustedRef.current = false;
    retryInFlightRef.current = false;
    clearRetryTimer();

    if (!path) {
      setUrl(null);
      setLoadingState(false);
      setImageLoaded(false);
      return;
    }

    const cached = peekAvatarUrl(path);
    if (cached) {
      setUrl(cached);
      setLoadingState(false);
      setImageLoaded(false);
      return;
    }

    setUrl(null);
    setLoadingState(true);
    setImageLoaded(false);

    let cancelled = false;
    void resolveAvatarUrl(path).then((resolved) => {
      if (cancelled) return;
      setLoadingState(false);
      if (!resolved) {
        if (isAvatarPathUploadProtected(path)) {
          setLoadingState(true);
          setFailed(false);
          tryScheduleRetry(path, (resolvedLater) => {
            setUrl(resolvedLater);
            setReloadKey((k) => k + 1);
          });
          return;
        }
        setUrl(null);
        setFailed(true);
        // eslint-disable-next-line no-console
        console.warn('[UserAvatar] resolveAvatarUrl returned null for path:', path);
        // Background retry — recover from transient signing failures
        // without requiring a manual refresh.
        tryScheduleRetry(path, (resolvedLater) => {
          setUrl(resolvedLater);
          setReloadKey((k) => k + 1);
        });
        return;
      }
      setImageLoaded(false);
      setUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [path]);

  // Re-resolve when the same `path` is replaced in storage (e.g. user uploads
  // a new avatar that overwrites the existing object key).
  //
  // Race-condition note: this listener is intentionally registered ONCE for
  // the lifetime of the component (no `path` dependency). Re-subscribing on
  // every `path` change would briefly leave a tick where no listener is
  // attached — an `AVATAR_INVALIDATED_EVENT` fired in that window (e.g. a
  // post-upload broadcast that overlaps with the parent's profile refetch)
  // would be silently dropped, leaving the avatar stuck on the old image
  // until the next manual refresh. We read the current `path` via
  // `pathRef.current` to avoid that gap.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Match either the exact path, OR another extension under the same
    // `<uid>/avatar.*` prefix — covers the "user replaces .jpg with .webp"
    // case where the bound prop still points at the old extension until
    // the parent's profile data refetches.
    const samePrefix = (a: string, b: string): boolean => {
      const stripExt = (s: string) => s.replace(/\.(jpg|jpeg|png|webp)$/i, "");
      return stripExt(a) === stripExt(b);
    };
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ path?: string }>).detail;
      if (!detail?.path) return;
      const currentPath = pathRef.current;
      if (!currentPath) return;
      if (
        detail.path !== currentPath &&
        !samePrefix(detail.path, currentPath)
      ) {
        return;
      }
      retriedRef.current = false;
      // Fresh upload for this user — give the retry budget a clean slate.
      retriesExhaustedRef.current = false;
      retryInFlightRef.current = false;
      clearRetryTimer();
      setFailed(false);
      setLoadingState(true);
      setImageLoaded(false);
      // Resolve against the freshly-uploaded path (which may have a new
      // extension) so the new bytes are shown even before the parent's
      // profile prop catches up.
      const targetPath = detail.path;
      void resolveAvatarUrl(targetPath).then((resolved) => {
        // Drop the result if the prop has since moved to a path that no
        // longer shares the same `<uid>/avatar.*` prefix — otherwise we
        // would paint a stale image over a freshly bound user.
        const liveCurrent = pathRef.current;
        if (!liveCurrent) return;
        if (
          targetPath !== liveCurrent &&
          !samePrefix(targetPath, liveCurrent)
        ) {
          return;
        }
        setLoadingState(false);
        if (!resolved) {
          if (isAvatarPathUploadProtected(targetPath)) {
            setLoadingState(true);
            setFailed(false);
            tryScheduleRetry(targetPath, (resolvedLater) => {
              setUrl(resolvedLater);
              setReloadKey((k) => k + 1);
            });
            return;
          }
          setUrl(null);
          setFailed(true);
          tryScheduleRetry(targetPath, (resolvedLater) => {
            setUrl(resolvedLater);
            setReloadKey((k) => k + 1);
          });
          return;
        }
        setImageLoaded(false);
        setUrl(resolved);
        setReloadKey((k) => k + 1);
      });
    };
    window.addEventListener(AVATAR_INVALIDATED_EVENT, handler);
    return () => window.removeEventListener(AVATAR_INVALIDATED_EVENT, handler);
  }, []);

  /**
   * The signed URL embeds a short-lived JWT (`?token=…`). When it expires,
   * Storage returns 400/403 and the `<img>` fires `onError`. Instead of
   * dropping straight to the initials fallback, invalidate the cache and
   * resolve a fresh URL exactly once. If that also fails, accept defeat.
   */
  const handleImageError = () => {
    // No path or budget already gone → render the fallback and STOP.
    // This is the critical anti-flicker guard: an unresolvable storage
    // object would otherwise loop (signed URL → 404 → onError → invalidate
    // → new signed URL → 404 → …) and the avatar would visibly toggle.
    if (!path || retriedRef.current || retriesExhaustedRef.current) {
      setFailed(true);
      return;
    }
    retriedRef.current = true;
    invalidateAvatarCache(path);
    // Keep the current `<img>` mounted while the retry runs — flipping to
    // the skeleton/fallback here is what causes the visible "zucken".
    // We only switch UI state once the retry result is in.
    retryInFlightRef.current = true;

    void resolveAvatarUrl(path).then((resolved) => {
      retryInFlightRef.current = false;
      // Guard against a `path` change happening mid-retry.
      if (lastPathRef.current !== path) return;
      if (!resolved) {
        retriesExhaustedRef.current = true;
        setUrl(null);
        setFailed(true);
        return;
      }
      setFailed(false);
      setImageLoaded(false);
      setUrl(resolved);
      setReloadKey((k) => k + 1);
    });
  };

  const initials = useMemo(
    () => computeInitials(displayName, alt),
    [displayName, alt],
  );

  const px = resolveSizePx(size);
  const sizeStyle: React.CSSProperties = {
    width: `${px}px`,
    height: `${px}px`,
    fontSize: `${px}px`, // anchor for the `0.45em` initials sizing.
  };
  const baseClass = cn(
    "inline-block shrink-0 overflow-hidden",
    SHAPE_CLASS[shape],
    ring && "ring-1 ring-border",
  );
  // `bg-background` gives transparent PNG avatars a clean, theme-aware
  // backplate so they don't pick up the dark card behind them.
  const mediaShellClass = cn(baseClass, "relative isolate bg-background");
  const mediaFillClass = cn("absolute inset-0 w-full h-full", SHAPE_CLASS[shape]);

  let node: ReactNode;
  if (loadingState && showLoadingState && !failed) {
    // Skeleton: same box, same shape — no layout shift on resolve.
    node = (
      <AvatarSkeleton
        className={cn(baseClass, className)}
        style={sizeStyle}
        ariaLabel={alt || displayName || "Avatar wird geladen"}
      />
    );
  } else if (!url || failed) {
    if (fallback !== undefined) {
      // Legacy callers provide their own fully-styled fallback; honour it.
      node = (
        <span className={cn(baseClass, className)} style={sizeStyle}>
          {fallback}
        </span>
      );
    } else {
      node = (
        <DefaultAvatarFallback
          className={cn(baseClass, className)}
          style={sizeStyle}
          initials={initials}
          ariaLabel={alt || displayName || "Avatar"}
        />
      );
    }
  } else {
    // Always append a cache-busting `v=` parameter. The signed URL itself
    // rotates per signing call (new JWT `?token=…`), but the underlying
    // storage object is served with `cache-control: 3600`, so browser/CDN
    // can keep returning the OLD bytes for the SAME object key after a
    // replace. The `v=` query parameter is part of the request URL the
    // browser/CDN keys its cache on, so bumping it guarantees a fresh
    // fetch. We seed `v` with a hash of the signed URL (stable across
    // re-renders → no needless re-downloads) and bump it on every
    // `reloadKey` increment (after an upload/replace event).
    const versionTag = `${hashString(url)}_${reloadKey}`;
    const imgSrc = `${url}${url.includes("?") ? "&" : "?"}v=${versionTag}`;
    node = (
      <span className={cn(mediaShellClass, className)} style={sizeStyle}>
        {fallback !== undefined ? (
          <span
            className={cn(
              mediaFillClass,
              "transition-opacity duration-200",
              imageLoaded ? "opacity-0" : "opacity-100",
            )}
            aria-hidden="true"
          >
            {fallback}
          </span>
        ) : (
          <span
            className={cn(
              mediaFillClass,
              "transition-opacity duration-200",
              imageLoaded ? "opacity-0" : "opacity-100",
            )}
            aria-hidden="true"
          >
            <DefaultAvatarFallback
              className={cn("w-full h-full", SHAPE_CLASS[shape])}
              initials={initials}
              ariaLabel={alt || displayName || "Avatar"}
            />
          </span>
        )}
        <img
          src={imgSrc}
          alt={alt || displayName || ""}
          loading={loading}
          className={cn(
            mediaFillClass,
            "object-cover transition-opacity duration-200",
            imageLoaded ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setImageLoaded(true)}
          onError={handleImageError}
        />
      </span>
    );
  }

  if (tooltip == null || tooltip === false || tooltip === "") return node;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex outline-none">
            {node}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Exposed for tests + advanced callers that want the same heuristic. */
export { computeInitials };